import React from 'react';
import { useSelector } from 'react-redux';
import { Controller, FieldError, useFieldArray, useFormContext } from 'react-hook-form';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { HtmlTable } from '../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { InputNumberMask } from '../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { IconButton } from '../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { FormValues } from '../../UpsertGrantModal.meta';
import { useStockOptionsContext } from '../../../../context/useStockOptionsContext';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { selectCurrentClient } from '../../../../../../CommonSelectors/CommonAreaSelector';

export const VestingSchedule = () => {
  const { options } = useStockOptionsContext();

  const formBag = useFormContext<FormValues>();

  const fieldArrayBag = useFieldArray<FormValues, 'vestingList', 'key'>({
    control: formBag.control,
    name: 'vestingList',
    keyName: 'key',
  });
  const watchFieldArray = formBag.watch('vestingList');
  const vestingList = fieldArrayBag.fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const owner = formBag.watch('owner');

  const isPrimary = owner === options.OwnerOptions.People[0];
  const isSpouse = owner === options.OwnerOptions.People[1];

  const vestingYearOptions = options.VestingYearOptions.map(year => {
    let rightLabel: string | undefined;

    if (isPrimary && primaryYob) {
      rightLabel = `Age ${year - primaryYob}`;
    } else if (isSpouse && spouseYob) {
      rightLabel = `Age ${year - spouseYob}`;
    }

    return {
      label: year.toString(),
      value: year,
      rightLabel,
    };
  });

  const totalShares = formBag.watch('totalShares');

  const totalVestedShares = vestingList.reduce((acc, vest) => acc + (vest.VestedShares ?? 0), 0);
  const sharesToAllocate = (totalShares ?? 0) - totalVestedShares;

  return (
    <Box pt="12">
      {Object.values(formBag.formState.errors).map(error => (
        <Alert variant="error" message={(error as FieldError).message} mb="16" mx="24" />
      ))}

      <Text color="neutral.500" px="24" pb="16">
        This vesting schedule assumes{' '}
        <Text as="span" fontWeight={600} color="neutral.500">
          {sharesToAllocate < 0 ? 0 : sharesToAllocate}
        </Text>{' '}
        shares have already vested.
      </Text>

      <HtmlTable variant="embedded" edgePadding={24}>
        <table>
          <colgroup>
            <col width="50%" />
            <col width="50%" />
            <col width="0%" />
          </colgroup>

          <thead>
            <tr>
              <th>
                <Text variant="smallCaps" textAlign="left">
                  Vest year
                </Text>
              </th>
              <th>
                <Text variant="smallCaps" textAlign="left">
                  Shares granted
                </Text>
              </th>

              <th>
                <Box width={36} />
              </th>
            </tr>
          </thead>

          <tbody>
            {vestingList.map((vest, index) => {
              return (
                <tr key={vest.key}>
                  <td>
                    <Controller
                      control={formBag.control}
                      name={`vestingList.${index}.VestingYear`}
                      render={({ field: { ref, onChange } }) => (
                        <Select
                          innerRef={ref}
                          value={
                            typeof vest.VestingYear === 'number'
                              ? ({
                                  label: vest.VestingYear.toString(),
                                  value: vest.VestingYear,
                                } as any)
                              : null
                          }
                          onChange={opt => onChange(opt?.value)}
                          options={vestingYearOptions}
                          aria-label="Vesting Year"
                          isPortal
                        />
                      )}
                    />
                  </td>

                  <td>
                    <Controller
                      control={formBag.control}
                      name={`vestingList.${index}.VestedShares`}
                      render={({ field: { onChange, ...field } }) => (
                        <InputNumberMask
                          {...field}
                          value={vest.VestedShares}
                          allowNegative={false}
                          thousandSeparator={false}
                          aria-label="Vested shares"
                          onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                        />
                      )}
                    />
                  </td>

                  <td>
                    <IconButton
                      icon="far fa-minus-circle"
                      onClick={() => {
                        if (vestingList.length === 1) {
                          fieldArrayBag.update(0, {});
                        } else {
                          fieldArrayBag.remove(index);
                        }
                      }}
                      variant="subtleInactive"
                      aria-label="Remove"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </HtmlTable>

      <Button
        leftIcon="fas fa-plus"
        variant="link"
        onClick={() => {
          const lastItem = vestingList[vestingList.length - 1];

          if (lastItem) {
            fieldArrayBag.append({
              VestedShares:
                typeof lastItem.VestedShares === 'number'
                  ? Math.min(lastItem.VestedShares, sharesToAllocate < 0 ? 0 : sharesToAllocate)
                  : undefined,
              VestingYear:
                typeof lastItem.VestingYear === 'number' && lastItem.VestingYear >= 2000
                  ? lastItem.VestingYear + 1
                  : undefined,
            });
          } else {
            fieldArrayBag.append({});
          }
        }}
        my="16"
        ml="24"
      >
        Add Another Vesting Schedule
      </Button>
    </Box>
  );
};
